/**
 * What a bot knows about the enemy under fog: the team's current line of
 * sight, every teammate's ghost markers (most recent sighting per enemy),
 * ghosts backtracked from shots that landed near it, how much of each planet
 * the team has surveyed, and how many probes the team has fired.
 *
 * Pure. The game keeps the incoming-fire intel map and passes it in; the
 * headless harness does the same.
 */

import { isBearingAlertDisabled } from '../core/matchEvents';
import { markTasksFor, markConfidenceTurns } from '../core/marks';
import type { GameState, PlayerState, BotFogContext, BotRoundContext, GhostMarker, IncomingFireIntel, Vec3, FireParams, PictureTarget } from '../types';
import { DEFAULT_BOT_TUNING, tuningFor } from './bot/tuning';
import { getTeamVisibleEnemies } from '../core/fog';
import { computeTurnOrder, getPlayerWorldPosition } from '../core/gameState';
import { vecNormalize } from '../math';
import { createAwareness, observe, eventsFromGameState, trackConfidence } from '../core/awareness';
import { backtrackFireSource } from './bot/intel';
import { dhypot } from '../core/dmath';
import { commsPictureOn } from '../core/installations';

/** How close a teammate's aim point must be to an enemy to count as "queued on" that enemy. */
const QUEUED_TARGET_RADIUS = 8;

/**
 * Round flows: what `player` may know about the round being planned, from the
 * host's queue and planned moves. Own-team knowledge is the team's own queue;
 * enemy knowledge is limited to what a human would see — the planned-move marker
 * of a visible unit, and who has already taken their planning turn. Returns
 * undefined outside the round flows.
 */
/**
 * What a host hands its bots: the fog context when bot fog is on, plus the round
 * context in the plan-together flows. With bot fog off there is no fog context —
 * but the round context must still reach the bot (the queue-aware planner reads
 * `fogContext.round`), so the host gets a full-visibility context carrying it:
 * every living enemy visible, no ghosts, which the bot modules treat exactly as
 * "no fog". Game and the headless host both call this instead of mirroring it.
 */
export function buildBotHostContext(
  state: GameState,
  player: PlayerState,
  incomingIntel: IncomingFireIntel[] | undefined,
  useWasm: boolean,
  queue: ReadonlyArray<{ playerId: number; params?: FireParams; launch?: { pos: Vec3; vel: Vec3 } }>,
  plannedMoves: Iterable<{ playerId: number; walkTo?: Vec3; jump?: { launchPos: Vec3; launchVel: Vec3 } | unknown }>,
): BotFogContext | undefined {
  const flow = state.config.turnFlow;
  const roundFlow = flow === 'simultaneous' || flow === 'allPlan';
  let ctx = buildBotFogContext(state, player, incomingIntel, useWasm);
  if (!ctx) {
    if (!roundFlow || state.config.botFogEnabled) return undefined;
    const visibleEnemyIds = state.players.filter(p => p.alive && p.teamIndex !== player.teamIndex).map(p => p.id);
    ctx = { visibleEnemyIds, ghosts: [], turnNumber: state.turnNumber };
  }
  if (roundFlow) {
    const round = buildBotRoundContext(state, player, queue, plannedMoves, ctx.visibleEnemyIds);
    if (round) ctx.round = round;
  }
  return ctx;
}

export function buildBotRoundContext(
  state: GameState,
  player: PlayerState,
  queue: ReadonlyArray<{ playerId: number; params?: FireParams; launch?: { pos: Vec3; vel: Vec3 } }>,
  plannedMoves: Iterable<{ playerId: number; walkTo?: Vec3; jump?: { launchPos: Vec3; launchVel: Vec3 } | unknown }>,
  visibleEnemyIds: readonly number[],
): BotRoundContext | undefined {
  const flow = state.config.turnFlow;
  if (flow !== 'simultaneous' && flow !== 'allPlan') return undefined;
  const enemies = state.players.filter(p => p.alive && p.teamIndex !== player.teamIndex);
  const byId = new Map(state.players.map(p => [p.id, p]));

  // Teammates' queued shots → the enemy nearest each aim point
  const teamQueuedTargetIds: number[] = [];
  for (const q of queue) {
    const mate = byId.get(q.playerId);
    if (!mate || mate.id === player.id || mate.teamIndex !== player.teamIndex || !mate.botLastFireTargetPos) continue;
    const aim = mate.botLastFireTargetPos;
    let best = -1, bestD = QUEUED_TARGET_RADIUS;
    for (const e of enemies) {
      const pos = getPlayerWorldPosition(e, state.planets);
      const d = dhypot(pos.x - aim.x, pos.y - aim.y, pos.z - aim.z);
      if (d < bestD) { bestD = d; best = e.id; }
    }
    if (best >= 0 && !teamQueuedTargetIds.includes(best)) teamQueuedTargetIds.push(best);
  }

  // The team's own orders, in round-simulator shape (teammates only — the bot's own order is what it is planning)
  const teamOrders: NonNullable<BotRoundContext['teamOrders']> = [];
  const orderFor = (id: number) => { let o = teamOrders.find(x => x.playerId === id); if (!o) { o = { playerId: id }; teamOrders.push(o); } return o; };
  for (const q of queue) {
    const mate = byId.get(q.playerId);
    if (!mate || mate.id === player.id || mate.teamIndex !== player.teamIndex || !q.params) continue;
    orderFor(q.playerId).fire = { params: q.params, launch: q.launch };
  }

  // Visible enemies' planned walks (their marker is on the map for anyone who can see them)
  const enemyPlannedWalks: BotRoundContext['enemyPlannedWalks'] = [];
  const planned = new Set<number>();
  for (const m of plannedMoves) {
    planned.add(m.playerId);
    const mateMove = byId.get(m.playerId);
    if (mateMove && mateMove.id !== player.id && mateMove.teamIndex === player.teamIndex) {
      const j = m.jump as { launchPos?: Vec3; launchVel?: Vec3 } | undefined;
      if (j && j.launchPos && j.launchVel) orderFor(m.playerId).jump = { launchPos: j.launchPos, launchVel: j.launchVel };
      else if (m.walkTo) orderFor(m.playerId).walk = m.walkTo;
    }
    const e = byId.get(m.playerId);
    if (!e || !e.alive || e.teamIndex === player.teamIndex || !m.walkTo || m.jump) continue;
    if (!visibleEnemyIds.includes(e.id)) continue;
    enemyPlannedWalks.push({ playerId: e.id, surfaceDir: { ...m.walkTo } });
  }

  // Who cannot move again before this round resolves
  let enemyCommittedIds: number[];
  if (flow === 'simultaneous') {
    enemyCommittedIds = enemies.map(e => e.id);   // the enemy never acts during this team's round
  } else {
    const order = computeTurnOrder(state.players, state.config.turnOrderMode);
    const cur = order.indexOf(state.currentPlayerIndex);
    const acted = new Set<number>();
    for (let i = 0; i < order.length && i < cur; i++) acted.add(state.players[order[i]].id);
    for (const q of queue) acted.add(q.playerId);
    for (const id of planned) acted.add(id);
    enemyCommittedIds = enemies.filter(e => acted.has(e.id)).map(e => e.id);
  }
  // The team plan's shot for this unit, if it was made this round and the unit still stands where it planned
  const ps = player.botPlannedShot;
  const sd = ps ? vecNormalize(ps.fromSurfaceDir) : null;
  const pd = vecNormalize(player.surfaceDir);
  const plannedShot = ps && ps.round === state.turnNumber && ps.fromPlanetIndex === player.planetIndex && sd && (sd.x * pd.x + sd.y * pd.y + sd.z * pd.z) > 0.9999
    ? { ...ps.fire } : undefined;
  return { flow, teamQueuedTargetIds, enemyPlannedWalks, enemyCommittedIds, ...(teamOrders.length ? { teamOrders } : {}), ...(plannedShot ? { plannedShot } : {}) };
}

/** Probes a team has launched so far (queued ones included, refunded ones not). */
export function teamProbesFired(state: Pick<GameState, 'players'>, teamIndex: number): number {
  let n = 0;
  for (const p of state.players) if (p.teamIndex === teamIndex) n += p.probesFired ?? 0;
  return n;
}

export function buildBotFogContext(
  state: GameState,
  player: PlayerState,
  incomingIntel: IncomingFireIntel[] | undefined,
  useWasm: boolean,
): BotFogContext | undefined {
  if (!state.config.botFogEnabled) return undefined;
  // R14 (b): a comms blackout cuts the incoming-fire bearing for bots as it does the cue for people (the fairness rule)
  if (isBearingAlertDisabled(state)) incomingIntel = undefined;
  // Fog array is indexed by player array position, NOT player.id
  const playerIdx = state.players.findIndex(p => p.id === player.id);
  if (playerIdx < 0) return undefined;
  const playerFog = state.fog[playerIdx];
  if (!playerFog) return undefined;

  const visibleIds = getTeamVisibleEnemies(player, state.players, state.planets, useWasm, state);

  // Ghosts from every living teammate, most recent sighting per enemy
  const teamGhosts: GhostMarker[] = [];
  const seen = new Set<number>();
  for (let ti = 0; ti < state.players.length; ti++) {
    const teammate = state.players[ti];
    if (!teammate.alive || teammate.teamIndex !== player.teamIndex) continue;
    const tFog = state.fog[ti];
    if (!tFog) continue;
    for (const ghost of tFog.ghosts) {
      if (!seen.has(ghost.playerId)) {
        seen.add(ghost.playerId);
        teamGhosts.push({ ...ghost });
      } else {
        const existing = teamGhosts.find(g => g.playerId === ghost.playerId);
        if (existing && ghost.turnSeen > existing.turnSeen) {
          existing.position = ghost.position;
          existing.turnSeen = ghost.turnSeen;
        }
      }
    }
  }

  // Incoming fire → a backtracked source (37.20 ledger #5). A human gets where a shot came from, not who fired it:
  // the point refreshes a shooter the team already knows (in sight or remembered), and otherwise is an anonymous
  // contact — a place to shoot back at — never a named ghost.
  const fireContacts: Array<{ position: Vec3; turnSeen: number }> = [];
  if (incomingIntel && incomingIntel.length > 0) {
    for (const intel of incomingIntel) {
      const ghost = backtrackFireSource(intel, state.planets);
      if (!ghost) continue;
      const idx = teamGhosts.findIndex(g => g.playerId === ghost.playerId);
      if (idx >= 0) {
        if (ghost.turnSeen > teamGhosts[idx].turnSeen) teamGhosts[idx] = ghost;
      } else if (!visibleIds.includes(ghost.playerId)) {
        fireContacts.push({ position: ghost.position, turnSeen: ghost.turnSeen });
      }
    }
  }

  // Phase 58: the team's awareness from the same records — its fog ghosts, what is in sight, the reveal
  // arrays, and incoming fire backtracked to a source (as anonymous contacts: a bearing says where, not who)
  const backtracked = (incomingIntel ?? []).map(intel => backtrackFireSource(intel, state.planets)).filter((g): g is GhostMarker => !!g).map(g => ({ sourcePos: g.position, turn: g.turnSeen }));
  const knownEnemyCount = state.players.filter(p => p.alive && p.teamIndex !== player.teamIndex).length;   // public today (team sizes are shown)
  const awareness = observe(
    createAwareness(player.teamIndex, state.turnNumber, knownEnemyCount),
    eventsFromGameState(state, player.teamIndex, visibleIds, (id: number) => getPlayerWorldPosition(state.players.find(p => p.id === id)!, state.planets), backtracked),
  );
  // Per-planet reveal fraction, team-wide max (probe targeting) — read from the awareness's coverage (the same numbers)
  const planetRevealFractions = state.planets.map((_, i) => awareness.coverage[i]?.revealedFraction ?? 0);

  // Team-wide probes launched (a counter on each unit, kept where every shot is paid for —
  // core/actions applyFireCost — so it is the same in the game and the harness, queued probes included)
  const probeAttempts = teamProbesFired(state, player.teamIndex);

  // §15.2: with the team picture on, every track it holds is a thing to fire at, with its precision
  const pictureTargets: PictureTarget[] = [];
  const pic = player.botConfig ? tuningFor(player.botConfig).picture : DEFAULT_BOT_TUNING.picture;   // the catalogue: the bot's own picture knobs
  if (state.config.teamPicture || commsPictureOn(state, player.teamIndex)) {   // X18 step 2: or a held comms installation
    for (const t of awareness.tracks) {
      if (!t.surfaceDir || t.planetIndex < 0) continue;
      if (t.unitId !== undefined && visibleIds.includes(t.unitId)) continue;   // in sight: fired on as it is
      const confidence = trackConfidence(t, state.turnNumber);
      if (confidence < pic.minConfidence) continue;
      const age = Math.max(0, state.turnNumber - t.turn);
      pictureTargets.push({ ...(t.unitId !== undefined ? { unitId: t.unitId } : {}), planetIndex: t.planetIndex, surfaceDir: { ...t.surfaceDir }, spread: pic.spread[t.source] + pic.spreadPerTurn * age, turnSeen: t.turn, confidence });
    }
  }

  // X2: the team's marks are picture targets on the turn clock too, the tightest spread, aged by turn
  for (const m of markTasksFor(state, player.teamIndex)) {
    if (m.order.unitId !== undefined && visibleIds.includes(m.order.unitId)) continue;
    const confidence = markConfidenceTurns(m.order.turn ?? state.turnNumber, state.turnNumber);
    if (confidence < pic.minConfidence) continue;
    const age = Math.max(0, state.turnNumber - (m.order.turn ?? state.turnNumber));
    pictureTargets.push({ ...(m.order.unitId !== undefined ? { unitId: m.order.unitId } : {}), planetIndex: m.order.planetIndex, surfaceDir: { ...m.order.surfaceDir }, spread: pic.spread.mark + pic.spreadPerTurn * age, turnSeen: m.order.turn ?? state.turnNumber, confidence });
  }
  return {
    visibleEnemyIds: visibleIds,
    ghosts: teamGhosts,
    ...(fireContacts.length ? { fireContacts } : {}),
    ...(pictureTargets.length ? { pictureTargets } : {}),
    turnNumber: state.turnNumber,
    planetRevealFractions,
    probeAttempts,
    awareness,
  };
}
